import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Download, X, Monitor, Smartphone, Share, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

type PlatformType = 'chrome' | 'safari-mac' | 'safari-ios' | 'unsupported';

const STORAGE_KEYS = {
  DISMISSED: 'pwa-install-dismissed',
  INSTALLED: 'pwa-installed-confirmed',
};

const DAYS_UNTIL_RESHOW = 7;

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  // Detect platform
  const platform = useMemo<PlatformType>(() => {
    const ua = navigator.userAgent;
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isMac = navigator.platform?.toUpperCase().includes('MAC') || /Macintosh/.test(ua);
    
    if (isSafari && isIOS) return 'safari-ios';
    if (isSafari && isMac) return 'safari-mac';
    if (!isSafari) return 'chrome'; // Chrome, Edge, etc - supports beforeinstallprompt
    
    return 'unsupported';
  }, []);

  // Check if running as PWA (standalone mode)
  const checkIsStandalone = useCallback(() => {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true || // Safari iOS
      localStorage.getItem(STORAGE_KEYS.INSTALLED) === 'true'
    );
  }, []);

  // Check if should show based on dismissal time
  const shouldShowAfterDismissal = useCallback(() => {
    const dismissed = localStorage.getItem(STORAGE_KEYS.DISMISSED);
    if (!dismissed) return true;
    
    const dismissedAt = new Date(dismissed);
    const daysSinceDismissed = (Date.now() - dismissedAt.getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceDismissed > DAYS_UNTIL_RESHOW;
  }, []);

  useEffect(() => {
    // Check if already installed
    if (checkIsStandalone()) {
      setIsInstalled(true);
      return;
    }

    // For Safari (Mac/iOS), show prompt after delay if not dismissed recently
    if (platform === 'safari-mac' || platform === 'safari-ios') {
      if (shouldShowAfterDismissal()) {
        const timer = setTimeout(() => setShowPrompt(true), 3000);
        return () => clearTimeout(timer);
      }
      return;
    }

    // For Chrome/Edge - use native beforeinstallprompt
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      
      if (shouldShowAfterDismissal()) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
      localStorage.setItem(STORAGE_KEYS.INSTALLED, 'true');
      console.log('[PWA] App installed successfully');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [platform, checkIsStandalone, shouldShowAfterDismissal]);

  // Handle Chrome/Edge native install
  const handleNativeInstall = useCallback(async () => {
    if (!deferredPrompt) return;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('[PWA] User accepted the install prompt');
        setShowPrompt(false);
        localStorage.setItem(STORAGE_KEYS.INSTALLED, 'true');
      } else {
        console.log('[PWA] User dismissed the install prompt');
        localStorage.setItem(STORAGE_KEYS.DISMISSED, new Date().toISOString());
      }
    } catch (error) {
      console.error('[PWA] Install error:', error);
    }
    
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  // Handle Safari "I did it" confirmation
  const handleSafariConfirm = useCallback(() => {
    setShowPrompt(false);
    localStorage.setItem(STORAGE_KEYS.INSTALLED, 'true');
    console.log('[PWA] User confirmed Safari installation');
  }, []);

  // Handle dismiss (remind later)
  const handleDismiss = useCallback(() => {
    setShowPrompt(false);
    localStorage.setItem(STORAGE_KEYS.DISMISSED, new Date().toISOString());
  }, []);

  if (isInstalled || !showPrompt) return null;

  // Don't show for unsupported platforms
  if (platform === 'unsupported') return null;

  // Safari Mac instructions
  if (platform === 'safari-mac') {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 50, scale: 0.95 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50"
        >
          <Card className="bg-card border shadow-xl">
            <CardContent className="p-4">
              <button 
                onClick={handleDismiss} 
                className="absolute top-2 right-2 p-1 rounded-full hover:bg-muted transition-colors"
                aria-label="Fechar"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
              
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Monitor className="w-6 h-6 text-primary" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm mb-2">
                    Instalar Divus CRM no Mac
                  </h3>
                  
                  <div className="space-y-2 mb-4">
                    <p className="text-xs text-muted-foreground">
                      Para melhor experiência, adicione à Dock:
                    </p>
                    <ol className="text-xs space-y-1.5 text-foreground">
                      <li className="flex items-start gap-2">
                        <span className="font-medium text-primary">1.</span>
                        <span>Clique em <strong>"Arquivo"</strong> no menu do Safari</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-medium text-primary">2.</span>
                        <span>Selecione <strong>"Adicionar à Dock"</strong></span>
                      </li>
                    </ol>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleSafariConfirm} 
                      size="sm" 
                      className="flex-1"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Já fiz!
                    </Button>
                    <Button 
                      onClick={handleDismiss} 
                      size="sm" 
                      variant="outline"
                      className="flex-1"
                    >
                      Depois
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Safari iOS instructions
  if (platform === 'safari-ios') {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 50, scale: 0.95 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="fixed bottom-4 left-4 right-4 z-50"
        >
          <Card className="bg-card border shadow-xl">
            <CardContent className="p-4">
              <button 
                onClick={handleDismiss} 
                className="absolute top-2 right-2 p-1 rounded-full hover:bg-muted transition-colors"
                aria-label="Fechar"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
              
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Smartphone className="w-6 h-6 text-primary" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm mb-2">
                    Instalar Divus CRM
                  </h3>
                  
                  <div className="space-y-2 mb-4">
                    <p className="text-xs text-muted-foreground">
                      Para acesso rápido e notificações:
                    </p>
                    <ol className="text-xs space-y-1.5 text-foreground">
                      <li className="flex items-start gap-2">
                        <span className="font-medium text-primary">1.</span>
                        <span>Toque no ícone <Share className="w-3.5 h-3.5 inline text-primary" /> <strong>Compartilhar</strong></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-medium text-primary">2.</span>
                        <span>Role e toque em <strong>"Adicionar à Tela Inicial"</strong></span>
                      </li>
                    </ol>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleSafariConfirm} 
                      size="sm" 
                      className="flex-1"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Já fiz!
                    </Button>
                    <Button 
                      onClick={handleDismiss} 
                      size="sm" 
                      variant="outline"
                      className="flex-1"
                    >
                      Depois
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Chrome/Edge native prompt
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 50, scale: 0.95 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 z-50"
      >
        <Card className="bg-card border shadow-xl">
          <CardContent className="p-4">
            <button 
              onClick={handleDismiss} 
              className="absolute top-2 right-2 p-1 rounded-full hover:bg-muted transition-colors"
              aria-label="Fechar"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
            
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Smartphone className="w-6 h-6 text-primary" />
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm mb-1">
                  Instalar Divus CRM
                </h3>
                <p className="text-xs text-muted-foreground mb-3">
                  Instale o app para acesso rápido e receber notificações mesmo offline.
                </p>
                
                <Button 
                  onClick={handleNativeInstall} 
                  size="sm" 
                  className="w-full"
                  disabled={!deferredPrompt}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Instalar App
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
}
